import React from "react";
import { Card, Badge } from "react-bootstrap";
import { format, isValid, parse } from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAddressCard, faDisplay } from "@fortawesome/free-solid-svg-icons";
import imageNotFound from "../assets/img/image-not-found.png";
import {
  getEffectiveEventStatus,
  isRegistrationOpen,
} from "../assets/functions.js";
import { domainConfig } from "../config.js";
import { colorsX, renderActiveModules, statusX } from "./EventsComponents.js";

const platformConfig = {
  1: {
    icon: faDisplay,
    titleAs: "h6",
    iconClassName: "me-2",
  },
  2: {
    icon: faAddressCard,
    titleAs: "h4",
    iconClassName: "me-2",
  },
};

const pickFirstDefined = (...items) =>
  items.find((item) => item !== undefined && item !== null);

const hasText = (value) =>
  typeof value === "string" ? value.trim() !== "" : Boolean(value);

const formatSummaryDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return "";

  const start = parse(startDate, "dd/MM/yyyy HH:mm", new Date());
  const end = parse(endDate, "dd/MM/yyyy HH:mm", new Date());

  if (!isValid(start) || !isValid(end)) return "";

  return ` (${format(start, "dd/MM/yyyy")} - ${format(end, "dd/MM/yyyy")})`;
};

const pickFirstText = (...items) => items.find((item) => hasText(item)) || "";

const normalizeJsonObject = (value) => {
  if (value && typeof value === "object") return value;

  if (typeof value === "string") {
    try {
      const parsedValue = JSON.parse(value);
      return parsedValue && typeof parsedValue === "object" ? parsedValue : {};
    } catch (error) {
      return {};
    }
  }

  return {};
};

const isEnabled = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

const getEventLogoFilename = (platform, eventJson) => {
  const networking = eventJson?.networking || {};
  const credentialPresentation = eventJson?.credentialAccess?.apresentation || {};

  if (Number(platform) === 2 && isEnabled(networking.status)) {
    return pickFirstText(
      networking.logo,
      networking.logoNav,
      networking.apresentation?.logo,
      eventJson.logo,
      eventJson.logoNav,
    );
  }

  if (Number(platform) === 2 && isEnabled(eventJson?.credentialAccess?.status)) {
    return pickFirstText(
      credentialPresentation.logo,
      credentialPresentation.logoNav,
      eventJson.logo,
      eventJson.logoNav,
    );
  }

  return pickFirstText(eventJson.logo, eventJson.logoNav);
};

const getEventAssetSource = (platform, eventId, filename) => {
  if (!hasText(filename)) return imageNotFound;

  const normalizedFilename = String(filename).trim();
  if (/^(https?:|data:|blob:|\/\/)/i.test(normalizedFilename)) {
    return normalizedFilename;
  }

  const imageServer = String(domainConfig.imageServer || "").replace(/\/+$/, "");
  const folder = Number(platform) === 2 ? "plataforma-eventos" : "evento";

  if (!imageServer || !hasText(eventId)) return imageNotFound;

  return `${imageServer}/${folder}/${eventId}/${normalizedFilename.replace(/^\/+/, "")}`;
};

const getEventVisualConfig = (platform, eventJson, logoFilename) => {
  const credentialPresentation = eventJson?.credentialAccess?.apresentation || null;
  const useCredentialAccessBranding =
    Number(platform) === 2 && eventJson?.networking?.status === false;

  if (!useCredentialAccessBranding || !credentialPresentation) {
    return { ...eventJson, logo: logoFilename };
  }

  return {
    ...eventJson,
    logo: logoFilename,
    background:
      credentialPresentation.textSide?.background || eventJson.background,
    transparency:
      credentialPresentation.register?.transparency ?? eventJson.transparency,
    boxShadow:
      credentialPresentation.register?.boxShadow ?? eventJson.boxShadow,
  };
};

const EventSummaryLogo = ({ platform, eventId, eventJson, logoFilename }) => {
  const visualConfig = getEventVisualConfig(platform, eventJson, logoFilename);
  const background = visualConfig.background || {};
  const logoSource = getEventAssetSource(platform, eventId, visualConfig.logo);
  const backgroundSource = hasText(background.url)
    ? getEventAssetSource(platform, eventId, background.url)
    : "";
  const hasLogo = hasText(visualConfig.logo);
  const frameStyle = {
    backgroundColor: visualConfig.transparency,
    boxShadow: visualConfig.boxShadow
      ? `0 .2rem 0.75rem ${visualConfig.boxShadow}`
      : undefined,
    borderRadius: "5px",
  };
  const backgroundStyle =
    background.type === "image"
      ? {
          background: `url(${backgroundSource || imageNotFound}) no-repeat center center / cover`,
        }
      : !hasLogo && background.type !== "video"
        ? {
            background: `url(${imageNotFound}) no-repeat center center / cover`,
          }
        : { backgroundColor: background.color };

  return (
    <div className="event-summary-card-logo" style={backgroundStyle}>
      {background.type === "video" && backgroundSource && (
        <video
          className="event-summary-card-video"
          autoPlay
          loop
          muted
          playsInline
          src={backgroundSource}
        />
      )}
      <div className="event-summary-card-frame">
        <div className="event-summary-card-overlay" style={frameStyle}>
          {hasLogo && (
            <img
              className="event-summary-card-image"
              src={logoSource}
              alt="Logo do evento"
              onError={(event) => {
                if (event.currentTarget.src === imageNotFound) return;
                event.currentTarget.onerror = null;
                event.currentTarget.src = imageNotFound;
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const getApplicationBadgeLabel = (application) =>
  pickFirstDefined(
    application?.nomeAplicativo,
    application?.nome,
    application?.title,
    application?.titulo,
    application?.aplicativo_id
      ? `Aplicativo ${application.aplicativo_id}`
      : null,
  );

const EventSummaryCardComponent = ({
  platform,
  dataTable,
  values,
  title,
  token,
  status,
  totalParticipants,
  eventStartDate,
  eventEndDate,
  formData,
  modulesData,
  titleAs,
  icon,
  iconClassName,
  className,
  children,
}) => {
  const resolvedPlatform = pickFirstDefined(platform, dataTable?.platform, 1);
  const resolvedConfig =
    platformConfig[resolvedPlatform] || platformConfig[1];
  const HeadingTag = titleAs || resolvedConfig.titleAs;
  const resolvedTitle = pickFirstDefined(
    title,
    dataTable?.[resolvedPlatform === 2 ? "title" : "titulo"],
    dataTable?.title,
    dataTable?.titulo,
    "",
  );
  const resolvedToken = pickFirstDefined(token, dataTable?.token, "");
  const resolvedStatus = pickFirstDefined(
    status,
    values?.status,
    dataTable?.status,
  );
  const resolvedTotalParticipants = pickFirstDefined(
    totalParticipants,
    dataTable?.total_participantes,
    0,
  );
  const resolvedEventStartDate = pickFirstDefined(
    eventStartDate,
    values?.data_criacao,
    values?.creation_date,
    dataTable?.data_criacao,
    dataTable?.creation_date,
  );
  const resolvedEventEndDate = pickFirstDefined(
    eventEndDate,
    values?.data_termino,
    values?.end_date,
    dataTable?.data_termino,
    dataTable?.end_date,
  );
  const resolvedFormData = pickFirstDefined(
    formData,
    values?.json?.form,
    dataTable?.json?.form,
    {},
  );
  const resolvedModulesData = pickFirstDefined(
    modulesData,
    values?.json,
    dataTable?.json,
    {},
  );
  const effectiveStatus = getEffectiveEventStatus(
    resolvedStatus,
    resolvedEventEndDate,
  );
  const registrationIsOpen = isRegistrationOpen(
    resolvedFormData?.status,
    resolvedFormData?.register_end_date,
  );
  const resolvedApplications = pickFirstDefined(
    dataTable?.app_details,
    dataTable?.aplicativos,
    [],
  );
  const activeModules = renderActiveModules(resolvedModulesData);
  const applicationBadges =
    resolvedPlatform === 1
      ? Object.values(
          (resolvedApplications || []).reduce((accumulator, application, index) => {
            const key = pickFirstDefined(
              application?.aplicativo_id,
              application?.customizacao_id,
              `${index}`,
            );
            const label = getApplicationBadgeLabel(application);

            if (hasText(label) && !accumulator[key]) {
              accumulator[key] = { key, label };
            }

            return accumulator;
          }, {}),
        ).map((application) => (
          <Badge
            key={`application-${application.key}`}
            bg="primary"
            className="me-1 text-black"
          >
            {application.label}
          </Badge>
        ))
      : [];
  const badgeColor = colorsX[effectiveStatus] || "secondary";
  const headingIcon = icon || resolvedConfig.icon;
  const headingIconClassName =
    iconClassName === undefined
      ? resolvedConfig.iconClassName
      : iconClassName;
  const eventJson = normalizeJsonObject(
    pickFirstDefined(values?.json, dataTable?.json, {}),
  );
  const logoFilename = getEventLogoFilename(resolvedPlatform, eventJson);
  const eventId = pickFirstDefined(
    values?.event_id,
    values?.evento_id,
    dataTable?.event_id,
    dataTable?.evento_id,
  );

  return (
    <Card className={className}>
      <Card.Body>
        <div className="event-summary-card-layout">
          <EventSummaryLogo
            platform={resolvedPlatform}
            eventId={eventId}
            eventJson={eventJson}
            logoFilename={logoFilename}
          />
          <div className="event-summary-card-content">
            <HeadingTag className="mb-1">
              <FontAwesomeIcon
                icon={headingIcon}
                className={headingIconClassName}
              />
              {hasText(resolvedTitle) ? (
                <>{resolvedTitle}</>
              ) : (
                <span className="text-muted">(Sem título)</span>
              )}
            </HeadingTag>
            <p className="mb-0">
              <b>Token: </b>
              {resolvedToken}
            </p>
            <p className="mb-0">
              <b>Status: </b>
              <Badge
                bg={badgeColor}
                className={`text-${badgeColor === "primary" ? "dark" : "white"}`}
              >
                {statusX[effectiveStatus] || "-"}
              </Badge>
              {formatSummaryDateRange(
                resolvedEventStartDate,
                resolvedEventEndDate,
              )}
            </p>
            <p className="mb-0">
              <b>Inscrições: </b>
              <Badge
                bg={registrationIsOpen ? "success" : "black"}
                className="text-white me-1"
              >
                {registrationIsOpen ? "Em aberto" : "Encerrado"}
              </Badge>
              {resolvedFormData?.register_start_date &&
                resolvedFormData?.register_end_date &&
                formatSummaryDateRange(
                  resolvedFormData.register_start_date,
                  resolvedFormData.register_end_date,
                )}
            </p>
            <p className="mb-0">
              <b>Registros: </b>
              {resolvedTotalParticipants}
            </p>
            {resolvedPlatform === 1 && applicationBadges.length > 0 && (
              <p className="mb-0">
                <b>Aplicativos: </b>
                {applicationBadges}
              </p>
            )}
            {resolvedPlatform !== 1 && activeModules.length > 0 && (
              <p className="mb-0">
                <b>Módulos ativos: </b>
                {activeModules}
              </p>
            )}
            {children}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

const areEventSummaryCardPropsEqual = (previousProps, nextProps) =>
  previousProps.platform === nextProps.platform &&
  previousProps.dataTable === nextProps.dataTable &&
  previousProps.values === nextProps.values &&
  previousProps.title === nextProps.title &&
  previousProps.token === nextProps.token &&
  previousProps.status === nextProps.status &&
  previousProps.totalParticipants === nextProps.totalParticipants &&
  previousProps.eventStartDate === nextProps.eventStartDate &&
  previousProps.eventEndDate === nextProps.eventEndDate &&
  previousProps.formData === nextProps.formData &&
  previousProps.modulesData === nextProps.modulesData &&
  previousProps.titleAs === nextProps.titleAs &&
  previousProps.icon === nextProps.icon &&
  previousProps.iconClassName === nextProps.iconClassName &&
  previousProps.className === nextProps.className;

export const EventSummaryCard = React.memo(
  EventSummaryCardComponent,
  areEventSummaryCardPropsEqual,
);
